import api from '@dashboard/services/axiosApi'
import type { PlanAction } from '../types'
import { useConfigStore } from '@dashboard/stores/configStore'
import { computed, getCurrentScope, onScopeDispose, readonly, ref } from 'vue'
import { observeOperation } from '@dashboard/services/realtimeResourceObserver'
import { useRoute } from 'vue-router'

/**
 * Available inline task types that map to backend RightyInlineService task types
 */
export type InlineTaskType =
  | 'create_question'
  | 'create_offer'
  | 'generate_code'
  | 'generate_variants'
  | 'generate_personalization'
  | 'generate_sync_values'
  | 'generate_publish_message'
  | 'generate_internal_id'
  | 'generate_segment_combinations'
  | 'question_analysis'
  | 'bootstrap_flow'
  | 'personalize_content'
  | 'generate_conditions'
  | 'generate_widget_design'

/**
 * Response types that can be returned from inline operations
 */
export type InlineResultType = 'plan' | 'json' | 'code' | 'raw'

export interface BootstrapFlowValidationError {
  path: string
  code: string
  message: string
  purposeId?: string
}

/**
 * Result from an inline AI operation
 */
export interface InlineResult {
  success: boolean
  type: InlineResultType
  status?: 'ready' | 'generating' | 'failed'
  requestId?: string
  /** For plan type - the plan action to execute */
  plan?: PlanAction
  data?: Record<string, unknown>
  /** For code type - generated JavaScript code */
  code?: string
  /** For raw type or if no structured content found */
  content?: string
  /** Raw response content (always included) */
  rawContent?: string
  /** Error message if success is false */
  error?: string
  errorCode?: string
  validationErrors?: BootstrapFlowValidationError[]
}

/**
 * Request payload for inline operations
 */
export interface InlineRequest {
  /** The task type to execute */
  taskType: InlineTaskType
  /** The user's instruction */
  instruction: string
  /** Optional additional context for the task */
  context?: Record<string, unknown>
}

const parseStructuredContent = (content: string): Record<string, unknown> | null => {
  const trimmed = content.trim()
  const fencedMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)
  const candidate = fencedMatch && fencedMatch[1] !== undefined ? fencedMatch[1].trim() : trimmed

  if (!candidate) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(candidate)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Snapshot polling interval while a queued generation runs, in case realtime delivery is missed. */
const GENERATION_POLL_INTERVAL_MS = 3000
/** Consecutive transient status-check failures tolerated before giving up on a queued generation. */
const MAX_CONSECUTIVE_SNAPSHOT_ERRORS = 5

interface InlineErrorPayload {
  error?: string
  errorCode?: string
  validationErrors?: BootstrapFlowValidationError[]
}

const getInlineErrorPayload = (error: unknown): InlineErrorPayload => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return {}
  }

  const response = error.response
  if (!response || typeof response !== 'object' || !('data' in response)) {
    return {}
  }

  const { data } = response
  if (!isInlineRecord(data)) {
    return {}
  }

  return {
    error: typeof data.error === 'string' ? data.error : undefined,
    errorCode: typeof data.errorCode === 'string' ? data.errorCode : undefined,
    validationErrors: Array.isArray(data.validationErrors)
      ? data.validationErrors.filter(
          (issue): issue is BootstrapFlowValidationError =>
            !!issue &&
            typeof issue === 'object' &&
            typeof issue.path === 'string' &&
            typeof issue.code === 'string' &&
            typeof issue.message === 'string'
        )
      : undefined
  }
}

const getErrorMessage = (error: unknown, fallback: string) => {
  const payload = getInlineErrorPayload(error)
  if (payload.error) {
    return payload.error
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

const buildInlineErrorResult = (error: unknown, fallback: string): InlineResult => {
  const payload = getInlineErrorPayload(error)

  return {
    success: false,
    type: 'raw',
    error: getErrorMessage(error, fallback),
    errorCode: payload.errorCode,
    validationErrors: payload.validationErrors
  }
}

const isInlineRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object'

const toInlineResult = (data: unknown): InlineResult => {
  const source = isInlineRecord(data) ? data : {}
  const content = typeof source.content === 'string' ? source.content : undefined
  const validationErrors = Array.isArray(source.validationErrors)
    ? source.validationErrors.filter(
        (issue): issue is BootstrapFlowValidationError =>
          !!issue &&
          typeof issue === 'object' &&
          typeof issue.path === 'string' &&
          typeof issue.code === 'string' &&
          typeof issue.message === 'string'
      )
    : undefined

  const inlineResult: InlineResult = {
    success: source.success === true,
    type:
      source.type === 'plan' || source.type === 'json' || source.type === 'code' || source.type === 'raw'
        ? source.type
        : 'raw',
    status:
      source.status === 'ready' || source.status === 'generating' || source.status === 'failed'
        ? source.status
        : undefined,
    requestId: typeof source.requestId === 'string' ? source.requestId : undefined,
    plan: isInlineRecord(source.plan) ? (source.plan as unknown as PlanAction) : undefined,
    data: isInlineRecord(source.data) ? source.data : undefined,
    code: typeof source.code === 'string' ? source.code : undefined,
    content,
    rawContent: typeof source.rawContent === 'string' ? source.rawContent : undefined,
    error: typeof source.error === 'string' ? source.error : undefined,
    errorCode: typeof source.errorCode === 'string' ? source.errorCode : undefined,
    validationErrors
  }

  if (!inlineResult.data && content) {
    inlineResult.data = parseStructuredContent(content) ?? undefined
  }

  return inlineResult
}

/**
 * Composable for inline/headless Righty AI operations.
 *
 * This provides focused, single-shot AI interactions without conversation history.
 * Used for task-specific AI operations like creating questions, generating code, etc.
 *
 * @example
 * ```typescript
 * const { execute, executeStreaming, isGenerating, streamingContent, result } = useRightyInline()
 *
 * // Non-streaming execution
 * const result = await execute({
 *   taskType: 'create_question',
 *   instruction: 'Create a question about their business size',
 *   context: { existingQuestions: ['Industry', 'Role'] }
 * })
 *
 * if (result.success && result.plan) {
 *   // Execute the plan
 *   await executePlan(result.plan)
 * }
 *
 * // Streaming execution
 * await executeStreaming({
 *   taskType: 'generate_code',
 *   instruction: 'Calculate total from quantity and price'
 * })
 *
 * // Watch streamingContent for real-time updates
 * watchEffect(() => {
 *   console.log(streamingContent.value)
 * })
 * ```
 */
export function useRightyInline(options?: { teamPid?: string }) {
  // Only try to get route if no teamPid was provided
  // This allows the composable to work outside component context when teamPid is passed directly
  const route = options?.teamPid ? null : useRoute()
  const teamPid = computed(() => options?.teamPid || (route?.params?.teamPid as string) || '')

  // State
  const isGenerating = ref(false)
  const streamingContent = ref('')
  const result = ref<InlineResult | null>(null)
  const error = ref<string | null>(null)
  const activeOperationObservers = new Set<AbortController>()
  const abortActiveOperations = () => {
    for (const observer of activeOperationObservers) {
      observer.abort()
    }
    activeOperationObservers.clear()
  }
  if (getCurrentScope()) {
    onScopeDispose(abortActiveOperations)
  }

  /**
   * Wait for a queued inline generation to finish. Operation invalidations
   * refresh its snapshot immediately; polling every few seconds covers missed
   * or unavailable realtime delivery. Settles as failed when the scope is
   * disposed or reset() aborts the observer.
   */
  function awaitGeneration(requestedTeamPid: string, requestId: string, fallbackError: string): Promise<InlineResult> {
    return new Promise<InlineResult>((resolve) => {
      const observer = new AbortController()
      activeOperationObservers.add(observer)
      let settled = false
      let loadingSnapshot = false
      let refreshQueued = false
      let pollTimer: number | undefined
      let consecutiveSnapshotErrors = 0

      const settle = (nextResult: InlineResult) => {
        if (settled) {
          return
        }
        settled = true
        window.clearTimeout(pollTimer)
        activeOperationObservers.delete(observer)
        observer.abort()
        if (!nextResult.success) {
          error.value = nextResult.error || fallbackError
        }
        result.value = nextResult
        resolve(nextResult)
      }

      const refreshSnapshot = async () => {
        if (settled) {
          return
        }
        if (loadingSnapshot) {
          refreshQueued = true
          return
        }

        window.clearTimeout(pollTimer)
        loadingSnapshot = true
        try {
          const { data: snapshot } = await api.get(`/api/${requestedTeamPid}/righty/inline/async/${requestId}`)
          consecutiveSnapshotErrors = 0
          const snapshotResult = toInlineResult(snapshot)
          if (snapshotResult.status === 'ready' || snapshotResult.status === 'failed') {
            settle(snapshotResult)
          }
        } catch (snapshotError: unknown) {
          // Network failures, 429, and 5xx leave the queued job running; keep
          // polling through a few of them. Other responses (403/404) are final.
          consecutiveSnapshotErrors += 1
          const status =
            isInlineRecord(snapshotError) &&
            isInlineRecord(snapshotError.response) &&
            typeof snapshotError.response.status === 'number'
              ? snapshotError.response.status
              : undefined
          const transient = status === undefined || status === 429 || status >= 500
          if (!transient || consecutiveSnapshotErrors >= MAX_CONSECUTIVE_SNAPSHOT_ERRORS) {
            settle(buildInlineErrorResult(snapshotError, 'Failed to execute inline task'))
          }
        } finally {
          loadingSnapshot = false
          if (refreshQueued && !settled) {
            refreshQueued = false
            void refreshSnapshot()
          } else if (!settled) {
            pollTimer = window.setTimeout(() => {
              void refreshSnapshot()
            }, GENERATION_POLL_INTERVAL_MS)
          }
        }
      }
      observer.signal.addEventListener(
        'abort',
        () => {
          if (!settled) {
            settle({
              success: false,
              type: 'raw',
              status: 'failed',
              error: 'AI operation was cancelled.'
            })
          }
        },
        { once: true }
      )

      observeOperation(
        requestId,
        () => {
          void refreshSnapshot()
        },
        observer.signal
      )
      // Completion may precede the subscription, and realtime may be unavailable.
      void refreshSnapshot()
    })
  }

  /**
   * Execute an inline AI task (non-streaming).
   * Returns when the AI response is complete. When the server hands a
   * timed-out request to the queued generation path, waits for that result.
   */
  async function execute(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }
    const requestedTeamPid = teamPid.value

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      // Include this tab's id so any DraftConfigUpdated broadcasts emitted while
      // the inline AI runs can be self-suppressed by this editor — preventing
      // a force-reload that wipes in-progress edits (ENG-1020). Best-effort:
      // configStore is unavailable in non-component contexts (e.g. background
      // jobs that pass options.teamPid directly), so we tolerate failure.
      let tabId: string | null = null
      try {
        tabId = useConfigStore().tabId?.() ?? null
      } catch {
        // configStore not initialized in this context — broadcast falls back to
        // current behavior (no self-suppression). The forbidden-tools guard on
        // the backend still ensures inline AI cannot publish.
      }

      const { data } = await api.post(`/api/${requestedTeamPid}/righty/inline`, {
        taskType: request.taskType,
        instruction: request.instruction,
        context: request.context || {},
        tabId
      })

      const inlineResult = toInlineResult(data)
      if (inlineResult.status === 'generating' && inlineResult.requestId) {
        return await awaitGeneration(requestedTeamPid, inlineResult.requestId, 'Failed to execute inline task')
      }
      result.value = inlineResult
      return inlineResult
    } catch (err: unknown) {
      const errorResult = buildInlineErrorResult(err, 'Failed to execute inline task')
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Execute an inline AI task with SSE streaming.
   * Updates streamingContent in real-time as the response arrives.
   */
  async function executeStreaming(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      // Get XSRF token from cookie (Laravel Sanctum pattern)
      const xsrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1]

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      }
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken)
      }

      // Include this tab's id for self-echo suppression on any DraftConfigUpdated
      // broadcasts emitted by tools the inline run invokes (ENG-1020).
      let tabId: string | null = null
      try {
        tabId = useConfigStore().tabId?.() ?? null
      } catch {
        // configStore not initialized; backend forbidden-tools guard still applies.
      }

      const backendHost = ''
      const response = await fetch(`${backendHost}/api/${teamPid.value}/righty/inline/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          taskType: request.taskType,
          instruction: request.instruction,
          context: request.context || {},
          tabId
        }),
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let fullText = ''
      let lastWasToolResult = false

      if (reader) {
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const events = buffer.split('\n\n')
          buffer = events.pop() || '' // Keep incomplete event in buffer

          for (const event of events) {
            if (!event.trim()) {
              continue
            }

            // Parse event type and data
            const lines = event.split('\n')
            let eventType = ''
            let eventData = ''

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7)
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6)
              }
            }

            if (!eventData) {
              continue
            }

            try {
              const data = JSON.parse(eventData)

              switch (eventType) {
                // OpenRouter streaming text chunks. Each chunk carries the
                // next slice of model output in `data.content`. Tool-result
                // paragraph spacing is handled by the backend, which now
                // emits a `text` event with `"\n\n"` between tool segments.
                case 'text':
                  if (lastWasToolResult && fullText.length > 0) {
                    if (!fullText.endsWith('\n\n')) {
                      fullText += fullText.endsWith('\n') ? '\n' : '\n\n'
                    }
                  }
                  lastWasToolResult = false
                  fullText += data.content || ''
                  streamingContent.value = fullText
                  break

                case 'tool_complete':
                case 'tool_result':
                  lastWasToolResult = true
                  break

                case 'thinking':
                case 'tool_start':
                  // Diagnostic events — no user-visible state change needed.
                  break

                case 'complete':
                case 'stream_end':
                  // Authoritative final text from the backend (if provided).
                  // Some backends send the canonical buffer here; prefer it
                  // over our accumulated chunks when present.
                  if (typeof data.text === 'string' && data.text.length > 0) {
                    fullText = data.text
                    streamingContent.value = fullText
                  }
                  break

                case 'error':
                  error.value = data.message || 'An error occurred'
                  break
              }
            } catch {
              // Ignore parse errors for malformed events
              console.warn('Failed to parse SSE event:', eventData)
            }
          }
        }
      }

      // Parse the final response to extract structured content
      const inlineResult = parseStreamingResponse(fullText)
      result.value = inlineResult
      return inlineResult
    } catch (err: unknown) {
      const errorResult = buildInlineErrorResult(err, 'Failed to execute streaming inline task')
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Parse streaming response text to extract structured content (plan, config, json, code)
   */
  function parseStreamingResponse(responseText: string): InlineResult {
    // Check for plan blocks
    const planMatch = responseText.match(/```plan\s*([\s\S]*?)```/)
    if (planMatch && planMatch[1] !== undefined) {
      try {
        const planData: PlanAction | null = JSON.parse(planMatch[1].trim())
        if (planData) {
          return {
            success: true,
            type: 'plan',
            plan: planData,
            rawContent: responseText
          }
        }
      } catch {
        // Fall through to other patterns
      }
    }

    // Check for JSON code blocks
    const jsonMatch = responseText.match(/```json\s*([\s\S]*?)```/)
    if (jsonMatch && jsonMatch[1] !== undefined) {
      try {
        const jsonData = JSON.parse(jsonMatch[1].trim())
        if (jsonData) {
          return {
            success: true,
            type: 'json',
            data: jsonData,
            rawContent: responseText
          }
        }
      } catch {
        // Fall through to other patterns
      }
    }

    // Check for JavaScript code blocks
    const codeMatch = responseText.match(/```(?:javascript|js)\s*([\s\S]*?)```/)
    if (codeMatch && codeMatch[1] !== undefined) {
      return {
        success: true,
        type: 'code',
        code: codeMatch[1].trim(),
        rawContent: responseText
      }
    }

    // Return raw response if no structured content found
    return {
      success: true,
      type: 'raw',
      content: responseText,
      rawContent: responseText
    }
  }

  /**
   * Reset the composable state
   */
  function reset() {
    abortActiveOperations()
    isGenerating.value = false
    streamingContent.value = ''
    result.value = null
    error.value = null
  }

  return {
    // State
    isGenerating: readonly(isGenerating),
    streamingContent: readonly(streamingContent),
    result: readonly(result),
    error: readonly(error),

    execute,
    executeStreaming,
    reset
  }
}
